import { useState } from 'react'
import {
  BOOST_CONTRACT_SUBSIDY_MANA,
  contractBoostAddsSubsidy,
} from 'common/boost'
import { Button } from '../buttons/button'
import { Modal } from '../layout/modal'
import { Col } from '../layout/col'
import { Row } from '../layout/row'
import { Contract } from 'common/contract'
import { useUser } from 'web/hooks/use-user'
import { api } from 'web/lib/api/api'
import toast from 'react-hot-toast'
import { BsRocketTakeoff } from 'react-icons/bs'
import { BOOST_COST_MANA } from 'common/economy'
import dayjs from 'dayjs'
import { Input } from '../widgets/input'
import { HOUR_MS } from 'common/util/time'
import { formatMoney } from 'common/util/format'
import { BoostAnalytics } from './boost-analytics'
import { ModActionBadge } from '../moderation/mod-action'
import { useAdmin, useAdminOrMod } from 'web/hooks/use-admin'
import Link from 'next/link'

export function AddBoostButton(props: {
  contract: Contract
  className?: string
}) {
  const { contract, className } = props
  const [showPurchase, setShowPurchase] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const user = useUser()

  if (!user) return null

  const disabled =
    contract.isResolved ||
    (contract.closeTime ?? Infinity) < Date.now() ||
    contract.visibility !== 'public'

  if (disabled) return null

  const handleButtonClick = () => {
    if (contract.boosted) {
      setShowAnalytics(true)
    } else {
      setShowPurchase(true)
    }
  }

  return (
    <>
      <Button
        onClick={handleButtonClick}
        color={contract.boosted ? 'gray-outline' : 'gradient-pink'}
        size="sm"
        className={className}
        data-boost-button
      >
        <BsRocketTakeoff className="mr-1 h-4 w-4" />
        {contract.boosted ? 'Boosted' : 'Boost'}
      </Button>

      <BoostPurchaseModal
        open={showPurchase}
        setOpen={setShowPurchase}
        contract={contract}
      />

      {showAnalytics && (
        <Modal open={showAnalytics} setOpen={setShowAnalytics} size="md">
          <Col className="bg-canvas-0 gap-4 rounded-lg p-6">
            <Row className="items-center gap-2 text-xl font-semibold">
              <BsRocketTakeoff className="h-6 w-6" />
              Boost Analytics
            </Row>
            <BoostAnalytics contract={contract} />
            <AdminRemoveBoostButton
              contract={contract}
              onRemoved={() => setShowAnalytics(false)}
            />
          </Col>
        </Modal>
      )}
    </>
  )
}

function AdminRemoveBoostButton(props: {
  contract: Contract
  onRemoved: () => void
}) {
  const { contract, onRemoved } = props
  const [isRemoving, setIsRemoving] = useState(false)
  const isAdmin = useAdmin()

  if (!isAdmin || !contract.boosted) return null

  const removeBoost = async () => {
    setIsRemoving(true)
    try {
      await api('remove-boost', {
        contractId: contract.id,
      })
      toast.success('Boost removed.')
      onRemoved()
    } catch (e) {
      console.error(e)
      toast.error(e instanceof Error ? e.message : 'Error removing boost')
    }
    setIsRemoving(false)
  }

  return (
    <Button
      color="purple-outline"
      size="xs"
      className="self-start"
      loading={isRemoving}
      disabled={isRemoving}
      onClick={removeBoost}
    >
      <ModActionBadge admin className="mr-1" />
      Remove boost
    </Button>
  )
}

function BoostPurchaseModal(props: {
  open: boolean
  setOpen: (open: boolean) => void
  contract: Contract
}) {
  const { open, setOpen, contract } = props
  const [loading, setLoading] = useState<string>()
  const now = Date.now()
  const [startTime, setStartTime] = useState(now)
  const user = useUser()
  const isAdminOrMod = useAdminOrMod()

  if (!user) return null

  const notEnoughFunds = (user.balance ?? 0) < BOOST_COST_MANA
  const paidBoostAddsSubsidy = contractBoostAddsSubsidy(contract)

  const purchaseBoost = async (paymentMethod: 'mana' | 'cash') => {
    setLoading(paymentMethod)
    try {
      const result = (await api('purchase-boost', {
        contractId: contract.id,
        startTime,
        method: paymentMethod,
      })) as { success: boolean; checkoutUrl?: string }

      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl
        return
      }

      toast.success(
        paidBoostAddsSubsidy
          ? `Market boosted! It will be featured on the homepage for 24 hours and receive ${formatMoney(
              BOOST_CONTRACT_SUBSIDY_MANA
            )} in subsidy.`
          : 'Market boosted! It will be featured on the homepage for 24 hours.'
      )
      setOpen(false)
    } catch (e) {
      console.error(e)
      toast.error(e instanceof Error ? e.message : 'Error purchasing boost')
    }
    setLoading(undefined)
  }

  const handleAdminFreeBoost = async () => {
    setLoading('admin-free')
    try {
      const result = (await api('purchase-boost', {
        contractId: contract.id,
        startTime,
        method: 'admin-free',
      })) as { success: boolean }

      if (result.success) {
        toast.success(
          'Market boosted for free! It will be featured on the homepage for 24 hours.'
        )
        setOpen(false)
      }
    } catch (e) {
      console.error(e)
      toast.error(e instanceof Error ? e.message : 'Error applying free boost')
    }
    setLoading(undefined)
  }

  return (
    <>
      <Modal open={open} setOpen={setOpen} size="sm">
        <Col className="bg-canvas-0 gap-4 rounded-lg p-6">
          <Row className="items-center gap-2 text-xl font-semibold">
            <BsRocketTakeoff className="h-6 w-6" />
            Boost this market
          </Row>

          <div className="text-ink-600">
            Boost this market's visibility on the homepage{' '}
            {Math.abs(startTime - now) < HOUR_MS
              ? 'for the next 24 hours'
              : `from ${dayjs(startTime).format('MMM D')} to ${dayjs(startTime)
                  .add(24, 'hours')
                  .format('MMM D')}`}
          </div>

          {paidBoostAddsSubsidy && (
            <div className="text-ink-600 text-sm">
              Paid boosts also add {formatMoney(BOOST_CONTRACT_SUBSIDY_MANA)} in
              subsidy to this contract.
            </div>
          )}

          <Row className="items-center gap-2">
            <div className="text-ink-600">Start time:</div>
            <Input
              type={'date'}
              className="dark:date-range-input-white"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const start = dayjs(e.target.value).startOf('day').valueOf()
                if (start < Date.now()) {
                  setStartTime(Date.now())
                } else {
                  setStartTime(start)
                }
              }}
              min={dayjs().format('YYYY-MM-DD')}
              max="3000-12-31"
              disabled={!!loading}
              value={dayjs(startTime).format('YYYY-MM-DD')}
            />
          </Row>

          <Button
            color="indigo"
            onClick={() => purchaseBoost('mana')}
            loading={loading === 'mana'}
            disabled={!!loading || notEnoughFunds}
          >
            Pay {formatMoney(BOOST_COST_MANA)}
          </Button>

          {isAdminOrMod && (
            <Row className="gap-2">
              <Button
                color="purple-outline"
                onClick={handleAdminFreeBoost}
                loading={loading === 'admin-free'}
                disabled={!!loading}
                className="flex-1"
              >
                <ModActionBadge className="mr-1" />
                Free Admin Boost
              </Button>
            </Row>
          )}

          {notEnoughFunds && (
            <div className="text-ink-600 flex items-center gap-2 text-sm">
              <span className="text-error">Insufficient balance</span>
              <Link
                href="/checkout"
                className="bg-primary-500 hover:bg-primary-600 rounded px-2 py-1 text-sm text-white"
              >
                Get mana
              </Link>
            </div>
          )}
        </Col>
      </Modal>
    </>
  )
}
